"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

// Storage keys
const STORAGE_KEY_ACTION_COUNT = "feedback_action_count";
const STORAGE_KEY_THRESHOLD = "feedback_action_threshold";
const STORAGE_KEY_POPUP_SHOWN = "feedback_popup_shown_session";

export type FeedbackTriggerSource = "auto" | "manual" | null;

interface FeedbackPromptContextType {
  /**
   * Track a user action that counts toward showing the feedback prompt.
   * Call this after successful actions like: challenge, schedule match, submit result, team drawer click
   */
  trackAction: () => void;
  /**
   * Why the modal is being requested: "auto" from the action-count threshold,
   * "manual" from the main menu, or null when no request is pending.
   * Consumers gate route/cooldown checks on this so manual triggers can bypass
   * client-side suppression (server still enforces flood prevention).
   */
  triggerSource: FeedbackTriggerSource;
  /**
   * Mark that the feedback prompt has been shown/dismissed
   */
  markFeedbackShown: () => void;
  /**
   * Open the feedback modal manually (e.g. from the main menu button).
   */
  openFeedback: () => void;
}

const FeedbackPromptContext = createContext<FeedbackPromptContextType>({
  trackAction: () => {},
  triggerSource: null,
  markFeedbackShown: () => {},
  openFeedback: () => {},
});

export const useFeedbackPrompt = () => useContext(FeedbackPromptContext);

interface FeedbackPromptProviderProps {
  children: React.ReactNode;
  /**
   * Whether the user has already been shown the feedback popup (from Firestore)
   */
  hasSeenFeedbackPopup: boolean;
  /**
   * Callback to persist that the user has seen the feedback popup
   */
  onFeedbackPopupShown?: () => void;
}

export function FeedbackPromptProvider({
  children,
  hasSeenFeedbackPopup,
  onFeedbackPopupShown,
}: FeedbackPromptProviderProps) {
  const [triggerSource, setTriggerSource] =
    useState<FeedbackTriggerSource>(null);
  const [initialized, setInitialized] = useState(false);
  const hasTriggeredRef = useRef(false);

  // Initialize from localStorage on mount
  useEffect(() => {
    // If user has already seen the popup (from Firestore), don't do anything
    if (hasSeenFeedbackPopup) {
      setInitialized(true);
      return;
    }

    // Check if already shown this session
    const shownThisSession = sessionStorage.getItem(STORAGE_KEY_POPUP_SHOWN);
    if (shownThisSession === "true") {
      setInitialized(true);
      return;
    }

    // Get or generate random threshold (10-30)
    let threshold = parseInt(
      localStorage.getItem(STORAGE_KEY_THRESHOLD) || "0",
      10,
    );
    if (!threshold) {
      threshold = Math.floor(Math.random() * 21) + 10; // Random 10-30
      localStorage.setItem(STORAGE_KEY_THRESHOLD, threshold.toString());
    }

    setInitialized(true);
  }, [hasSeenFeedbackPopup]);

  const trackAction = useCallback(() => {
    // Don't track if user already saw the popup or already triggered this session
    if (hasSeenFeedbackPopup || hasTriggeredRef.current) {
      return;
    }

    // Check if already shown this session
    const shownThisSession = sessionStorage.getItem(STORAGE_KEY_POPUP_SHOWN);
    if (shownThisSession === "true") {
      return;
    }

    // Get current count and increment
    const currentCount = parseInt(
      localStorage.getItem(STORAGE_KEY_ACTION_COUNT) || "0",
      10,
    );
    const newCount = currentCount + 1;
    localStorage.setItem(STORAGE_KEY_ACTION_COUNT, newCount.toString());

    // Check if we've reached the threshold
    const threshold = parseInt(
      localStorage.getItem(STORAGE_KEY_THRESHOLD) || "10",
      10,
    );

    if (newCount >= threshold && !hasTriggeredRef.current) {
      hasTriggeredRef.current = true;
      // Small delay before showing the popup (2 seconds)
      setTimeout(() => {
        setTriggerSource("auto");
      }, 2000);
    }
  }, [hasSeenFeedbackPopup]);

  const markFeedbackShown = useCallback(() => {
    setTriggerSource(null);
    // Mark as shown for this session
    sessionStorage.setItem(STORAGE_KEY_POPUP_SHOWN, "true");
    // Clear the action count and threshold for next time (if they come back)
    localStorage.removeItem(STORAGE_KEY_ACTION_COUNT);
    localStorage.removeItem(STORAGE_KEY_THRESHOLD);
    // Notify parent to persist to Firestore
    onFeedbackPopupShown?.();
  }, [onFeedbackPopupShown]);

  const openFeedback = useCallback(() => {
    setTriggerSource("manual");
  }, []);

  // Don't render children until initialized to prevent hydration issues
  if (!initialized) {
    return <>{children}</>;
  }

  return (
    <FeedbackPromptContext.Provider
      value={{
        trackAction,
        triggerSource,
        markFeedbackShown,
        openFeedback,
      }}
    >
      {children}
    </FeedbackPromptContext.Provider>
  );
}
